import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Chip,
  Divider,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import { SignaturePreview } from './SignaturePreview';
import type {
  UserSignature,
  SystemSignature,
} from '../../../../../store/reducers/signatures.store';

interface SignatureListProps {
  signatures: (UserSignature | SystemSignature)[];
  onEdit: (signature: UserSignature | SystemSignature) => void;
  onDelete: (id: string) => Promise<void>;
  onSetDefault: (id: string) => Promise<void>;
  loading?: boolean;
  isSystemSignatures?: boolean;
}

export const SignatureList: React.FC<SignatureListProps> = ({
  signatures,
  onEdit,
  onDelete,
  onSetDefault,
  loading = false,
  isSystemSignatures = false,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSignature, setSelectedSignature] = useState<
    UserSignature | SystemSignature | null
  >(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [expandedSignature, setExpandedSignature] = useState<string | null>(
    null
  );

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    signature: UserSignature | SystemSignature
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedSignature(signature);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSignature(null);
  };

  const handleEdit = () => {
    if (selectedSignature) {
      onEdit(selectedSignature);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    // Don't clear selectedSignature here - keep it for the delete operation
    setAnchorEl(null); // Only close the menu
  };

  const handleDeleteConfirm = async () => {
    if (!onDelete) {
      console.error('SignatureList: onDelete is undefined or null!');
      return;
    }

    if (selectedSignature) {
      try {
        await onDelete(selectedSignature.id);
        // Only clear selectedSignature after successful deletion
        setSelectedSignature(null);
      } catch (error) {
        console.error('SignatureList: onDelete call failed:', error);
        // Don't clear selectedSignature on error - let user try again
      }
    }
    setDeleteDialogOpen(false);
  };

  const handleSetDefault = async () => {
    if (selectedSignature) {
      try {
        await onSetDefault(selectedSignature.id);
      } catch (error) {
        console.error('SignatureList: onSetDefault call failed:', error);
      }
    }
    handleMenuClose();
  };

  const toggleExpanded = (signatureId: string) => {
    setExpandedSignature(
      expandedSignature === signatureId ? null : signatureId
    );
  };

  if (!Array.isArray(signatures) || signatures.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          px: 3,
        }}
      >
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No {isSystemSignatures ? 'team' : ''} signatures yet
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create your first {isSystemSignatures ? 'team' : ''} signature to get
          started
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {signatures.map((signature, index) => (
        <Card
          key={signature.id}
          sx={{
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            '&:hover': {
              boxShadow: theme.shadows[2],
            },
          }}
        >
          <CardContent>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-start"
            >
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Typography variant="h6" component="h3">
                    {signature.name}
                  </Typography>
                  {signature.isDefault && (
                    <Chip
                      icon={<StarIcon />}
                      label="Default"
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Created: {new Date(signature.createdAt).toLocaleDateString()}
                </Typography>

                {/* Display Team Signature specific fields */}
                {isSystemSignatures && (
                  <Box sx={{ mt: 1, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>From:</strong>{' '}
                      {(signature as SystemSignature).firstName}{' '}
                      {(signature as SystemSignature).lastName} (
                      {(signature as SystemSignature).email})
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    onClick={() => toggleExpanded(signature.id)}
                    sx={{ mb: 1 }}
                  >
                    {expandedSignature === signature.id
                      ? 'Hide Preview'
                      : 'Show Preview'}
                  </Button>

                  {expandedSignature === signature.id && (
                    <Box sx={{ mt: 1 }}>
                      <SignaturePreview
                        content={signature.content}
                        showTitle={false}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Box>
                <IconButton
                  onClick={(e) => handleMenuOpen(e, signature)}
                  disabled={loading}
                  size="small"
                >
                  <MoreVertIcon />
                </IconButton>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { minWidth: 160 },
        }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>

        {selectedSignature && !selectedSignature.isDefault && (
          <MenuItem onClick={handleSetDefault}>
            <StarIcon fontSize="small" sx={{ mr: 1 }} />
            Set as Default
          </MenuItem>
        )}

        <Divider />

        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete {isSystemSignatures ? 'Team' : ''} Signature
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete the signature "
            {selectedSignature?.name}"?
          </Typography>
          {selectedSignature?.isDefault && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This is your default signature. You may want to set another
              signature as default before deleting this one.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedSignature(null); // Clear selectedSignature when cancelling
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleDeleteConfirm();
            }}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
